import { useRef, useState } from 'react'
import gsap from 'gsap'
import { SplitText } from 'gsap/SplitText'
import { useGSAP } from '@gsap/react'
import CircularProgress from '@mui/material/CircularProgress'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded'
import flags from '../flags.js'
import styles from './LoginScreen.module.scss'

gsap.registerPlugin(SplitText)

// The same sign-in as the iPhone app: moving colour, "hello" drifting up in
// every language, a big welcome whose letters arrive one after another, and a
// frosted card with no outline.

// "Welcome" in each language the app speaks, in the app's order.
const GREETINGS = [
  ['eng', 'Welcome'],
  ['spa', 'Bienvenido'],
  ['fra', 'Bienvenue'],
  ['deu', 'Willkommen'],
  ['ita', 'Benvenuto'],
  ['por', 'Bem-vindo'],
  ['nld', 'Welkom'],
  ['swe', 'Välkommen'],
  ['tur', 'Hoş geldiniz'],
  ['rus', 'Добро пожаловать'],
  ['ell', 'Καλώς ήρθατε'],
  ['ara', 'أهلاً وسهلاً'],
  ['kor', '환영합니다'],
  ['jpn', 'ようこそ'],
  ['cmn', '欢迎'],
  ['vie', 'Chào mừng']
]

const HELLOS = ['Hello', 'Merhaba', '안녕하세요', 'Olá', 'Привет', 'こんにちは', 'Bonjour', 'Hola', 'Ciao', '你好', 'Hallo', 'Hej', 'Γεια σου', 'مرحبا', 'Xin chào', 'Hoi']

const prefersReducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

function Aurora() {
  return (
    <div className={styles.aurora} aria-hidden="true">
      {['teal', 'mustard', 'terra', 'olive', 'plum', 'sage'].map((c) => (
        <span key={c} className={`${styles.light} ${styles[c]}`} />
      ))}
      <div className={styles.shade} />
    </div>
  )
}

function DriftingHellos() {
  return (
    <div className={styles.drift} aria-hidden="true">
      {HELLOS.map((word, i) => {
        const duration = 60 + ((i * 7) % 5) * 14 // seconds to cross the page
        return (
          <span
            key={word}
            style={{
              left: `${8 + ((i * 61) % 84)}%`,
              fontSize: `${15 + ((i * 7) % 16)}px`,
              opacity: 0.06 + ((i * 3.7) % 7) / 100,
              animationDuration: `${duration}s`,
              animationDelay: `${-((i * 97) % duration)}s`
            }}
          >
            {word}
          </span>
        )
      })}
    </div>
  )
}

/** Each language's welcome in turn, its letters rising in out of a blur. */
function CyclingWelcome() {
  const [index, setIndex] = useState(0)
  const scope = useRef(null)
  const wordRef = useRef(null)
  const flagRef = useRef(null)
  const [code, text] = GREETINGS[index % GREETINGS.length]

  useGSAP(
    () => {
      // Shrink a long greeting until it fits on one line.
      const el = wordRef.current
      const range = document.createRange()
      range.selectNodeContents(el)
      let size = 56
      el.style.fontSize = `${size}px`
      while (range.getBoundingClientRect().width > el.clientWidth * 0.94 && size > 22) {
        size -= 2
        el.style.fontSize = `${size}px`
      }
      if (prefersReducedMotion()) {
        const t = setTimeout(() => setIndex((i) => i + 1), 3000)
        return () => clearTimeout(t)
      }
      // Arabic letters join, so it arrives word by word (right to left).
      const split = new SplitText(wordRef.current, { type: code === 'ara' ? 'words' : 'chars' })
      const pieces = code === 'ara' ? split.words : split.chars
      const each = Math.min(0.07, 0.9 / pieces.length)
      const tl = gsap.timeline({ onComplete: () => setIndex((i) => i + 1) })
      tl.from(flagRef.current, { opacity: 0, scale: 0.6, duration: 0.5, ease: 'back.out(2)' })
        .from(
          pieces,
          { opacity: 0, y: 38, scale: 0.7, filter: 'blur(10px)', duration: 0.75, ease: 'back.out(1.7)', stagger: each },
          0.05
        )
        .to(pieces, { opacity: 0, y: -24, filter: 'blur(10px)', duration: 0.5, ease: 'power2.in', stagger: each * 0.7 }, '+=1.8')
        .to(flagRef.current, { opacity: 0, scale: 0.8, duration: 0.35, ease: 'power2.in' }, '<0.2')
      return () => split.revert()
    },
    { dependencies: [index], scope, revertOnUpdate: true }
  )

  return (
    <div className={styles.welcome} ref={scope}>
      <div className={styles.flag} ref={flagRef}>
        {flags[code]}
      </div>
      <h1
        key={code}
        ref={wordRef}
        className={styles.word}
        lang={code}
        dir={code === 'ara' ? 'rtl' : 'ltr'}
      >
        {text}
      </h1>
    </div>
  )
}

function Field({ icon, ...input }) {
  return (
    <label className={styles.field}>
      {icon}
      <input {...input} />
    </label>
  )
}

export default function LoginScreen({
  username,
  setUsername,
  password,
  setPassword,
  onLogin,
  loading,
  message,
  register // { username, setUsername, password, setPassword, role, setRole, onRegister } when sign-up is open
}) {
  const scope = useRef(null)
  const canSubmit = username.trim() !== '' && password !== '' && !loading
  const failed = message && /failed/i.test(message)

  useGSAP(
    () => {
      if (prefersReducedMotion()) return
      gsap.from('[data-enter]', { opacity: 0, scale: 0.93, transformOrigin: '50% 0%', duration: 0.9, ease: 'back.out(1.4)', stagger: 0.12, delay: 0.1 })
    },
    { scope }
  )

  const submit = (e) => {
    e.preventDefault()
    if (canSubmit) onLogin()
  }

  return (
    <div className={styles.page} ref={scope}>
      <Aurora />
      <DriftingHellos />
      <main className={styles.column}>
        <div data-enter>
          <CyclingWelcome />
        </div>

        <form className={styles.card} onSubmit={submit} data-enter>
          <Field
            icon={<PersonRoundedIcon />}
            autoFocus
            type="text"
            name="username"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder="Username"
            aria-label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <Field
            icon={<LockRoundedIcon />}
            type="password"
            name="password"
            autoComplete="current-password"
            placeholder="Password"
            aria-label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          {message ? (
            <div className={failed ? styles.error : styles.note} role={failed ? 'alert' : 'status'}>
              {failed ? <ErrorRoundedIcon /> : null}
              <span>{message}</span>
            </div>
          ) : null}

          <button type="submit" className={styles.signIn} disabled={!canSubmit} data-ready={canSubmit || undefined} data-busy={loading || undefined}>
            {loading ? (
              <CircularProgress size={22} sx={{ color: '#fff' }} />
            ) : (
              <>
                Sign in <ArrowForwardRoundedIcon />
              </>
            )}
          </button>

          {register ? (
            <div className={styles.register}>
              <div className={styles.divider}>or create an account</div>
              <Field
                icon={<PersonRoundedIcon />}
                type="text"
                autoComplete="off"
                placeholder="New username"
                aria-label="New username"
                value={register.username}
                onChange={(e) => register.setUsername(e.target.value)}
              />
              <Field
                icon={<LockRoundedIcon />}
                type="password"
                autoComplete="new-password"
                placeholder="New password"
                aria-label="New password"
                value={register.password}
                onChange={(e) => register.setPassword(e.target.value)}
              />
              <label className={styles.field}>
                <span className={styles.selectLabel}>Account</span>
                <select value={register.role} onChange={(e) => register.setRole(e.target.value)}>
                  <option value="student">Student</option>
                  <option value="team">Team (work tools)</option>
                </select>
              </label>
              <button type="button" className={styles.secondary} onClick={register.onRegister}>
                Register
              </button>
            </div>
          ) : null}
        </form>
      </main>
    </div>
  )
}
